import React, { useState } from 'react';
import { InformationCircleIcon } from '@heroicons/react/24/outline';

/**
 * Small info-icon trigger that reveals a tooltip on hover/focus.
 * Use to explain dashboard metrics, stat tiles, or column headers
 * where the raw number alone doesn't tell the user what to do.
 *
 * Props:
 *   text       — required string or ReactNode shown in the tooltip
 *   side       — 'right' (default) | 'left' | 'top' | 'bottom'
 *   ariaLabel  — optional override; defaults to first 80 chars of text
 *   className  — extra classes on the trigger button
 *
 * Render-prop alternative: place this inline next to a label,
 *   e.g. <span>Avg score <MetricHint text="..." /></span>
 */
const SIDE_CLASSES = {
  right: 'left-full top-1/2 -translate-y-1/2 ml-2',
  left: 'right-full top-1/2 -translate-y-1/2 mr-2',
  top: 'bottom-full left-1/2 -translate-x-1/2 mb-2',
  bottom: 'top-full left-1/2 -translate-x-1/2 mt-2',
};

const MetricHint = ({ text, side = 'right', ariaLabel, className = '' }) => {
  const [open, setOpen] = useState(false);

  if (!text) return null;
  const labelText = typeof text === 'string' ? text : 'metric help';
  const aria = ariaLabel || (typeof text === 'string' ? text.slice(0, 80) : 'metric help');

  return (
    <span className="relative inline-flex items-center align-middle">
      <button
        type="button"
        aria-label={aria}
        aria-describedby={open ? 'metric-hint-popup' : undefined}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onClick={(e) => {
          e.preventDefault();
          setOpen((v) => !v);
        }}
        className={`text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 ${className}`}
        title={labelText}
      >
        <InformationCircleIcon className="w-4 h-4" />
      </button>
      {open && (
        <span
          id="metric-hint-popup"
          role="tooltip"
          className={`absolute z-50 ${SIDE_CLASSES[side] || SIDE_CLASSES.right} w-64 px-3 py-2 rounded-md bg-gray-900 text-white text-xs font-normal leading-relaxed shadow-lg whitespace-normal`}
        >
          {text}
        </span>
      )}
    </span>
  );
};

export default MetricHint;
